import { useMemo, useState, useEffect } from "react";
import { Search, User, CheckCircle2, X, Loader2, FileSpreadsheet, Printer, MessageCircle, AlertTriangle, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { fmtBRL, PAY_LABELS, PAY_COLORS, HEADER_BG, waLink } from "../lib/constants";
import { exportClientClosureCSV, printClientClosure } from "../lib/export";
import { printCobrancaPDF } from "../lib/cobrancaReport";
import CobrancaModal from "../components/CobrancaModal";

function fromISOInput(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

// Parse dd/mm/yyyy or dd/mm (assume current year). Returns Date or null.
function parseBR(dateStr, fallbackIso) {
  if (dateStr && typeof dateStr === "string") {
    const parts = dateStr.split("/");
    if (parts.length >= 2) {
      const dd = parseInt(parts[0], 10);
      const mm = parseInt(parts[1], 10);
      const yyyy = parts.length >= 3 ? parseInt(parts[2], 10) : new Date().getFullYear();
      if (!isNaN(dd) && !isNaN(mm) && !isNaN(yyyy)) {
        return new Date(yyyy, mm - 1, dd);
      }
    }
  }
  if (fallbackIso) {
    const d = new Date(fallbackIso);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
}

// Overdue rule: 30 days after end-of-month of the reference date.
// Returns { overdue: boolean, dueDate: Date|null, daysLate: number }
function overdueInfo(refDate) {
  if (!refDate) return { overdue: false, dueDate: null, daysLate: 0 };
  const endOfMonth = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0); // last day of the same month
  const due = new Date(endOfMonth.getFullYear(), endOfMonth.getMonth(), endOfMonth.getDate() + 30);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdue = today > due;
  const diff = Math.floor((today - due) / (1000 * 60 * 60 * 24));
  return { overdue, dueDate: due, daysLate: overdue ? diff : 0 };
}

export default function FechamentoView({ orders, pieceSales, clients, searchQuery, onRefresh }) {
  const [mode, setMode] = useState("client"); // "client" | "pending"
  const [dateFrom, setDateFrom] = useState(""); // yyyy-mm-dd from <input type=date>
  const [dateTo, setDateTo] = useState("");
  const [clientQuery, setClientQuery] = useState(searchQuery || "");
  const [selectedClient, setSelectedClient] = useState(null);
  const [store, setStore] = useState({});
  const [cobranca, setCobranca] = useState(null); // { client, rows, period }
  const [payFromCobranca, setPayFromCobranca] = useState(null); // { rows, total, client, initialDiscount }

  // Load store info once for PDF/WhatsApp headers
  useEffect(() => {
    api.get("/settings").then((r) => {
      setStore({
        storeName: r.data?.storeName || "Phone Lab · Assistência Técnica",
        phone: r.data?.phone || "",
        address: r.data?.address || "",
        cnpj: r.data?.cnpj || "",
      });
    }).catch(() => {});
  }, []);

  // Keep local query in sync with global header search
  useMemo(() => {
    if (searchQuery && mode === "client" && !selectedClient) setClientQuery(searchQuery);
  }, [searchQuery, mode, selectedClient]);

  // ─── Mode: por cliente ────────────────────────────────────────────────────
  const clientMatches = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return [];
    const list = clients
      .filter((c) => c.name.toLowerCase().includes(q) || c.phone.includes(clientQuery))
      .slice(0, 8);
    // also match by name typed even if not in clients registry — pull from orders
    const seenNames = new Set(list.map((c) => c.name.toLowerCase()));
    orders.forEach((o) => {
      const n = (o.clientName || "").toLowerCase();
      if (n.includes(q) && !seenNames.has(n)) {
        seenNames.add(n);
        list.push({ id: `ord-${o.id}`, name: o.clientName, phone: o.clientPhone });
      }
    });
    return list.slice(0, 10);
  }, [clientQuery, clients, orders]);

  const clientData = useMemo(() => {
    if (!selectedClient) return null;
    const nameLower = selectedClient.name.toLowerCase();
    const clientOrders = orders.filter(
      (o) =>
        (o.clientId && o.clientId === selectedClient.id) ||
        (o.clientPhone && selectedClient.phone && o.clientPhone === selectedClient.phone) ||
        o.clientName?.toLowerCase() === nameLower
    );
    const clientSales = pieceSales.filter(
      (s) => (s.clientStore || "").toLowerCase() === nameLower
    );
    const openOrders = clientOrders.filter((o) => o.status !== "delivered" || o.payMethod === "anotar");
    const paidOrders = clientOrders.filter((o) => o.status === "delivered" && o.payMethod && o.payMethod !== "anotar");
    const openSales = clientSales.filter((s) => s.payMethod === "anotar" || !s.payMethod);
    const paidSales = clientSales.filter((s) => s.payMethod && s.payMethod !== "anotar");
    const totalOpen =
      openOrders.reduce((s, o) => s + (Number(o.total) || 0), 0) +
      openSales.reduce((s, o) => s + (Number(o.value) || 0), 0);
    const totalPaid =
      paidOrders.reduce((s, o) => s + Math.max(0, (Number(o.total) || 0) - (Number(o.discount) || 0)), 0) +
      paidSales.reduce((s, o) => s + Math.max(0, (Number(o.value) || 0) - (Number(o.discount) || 0)), 0);
    return { clientOrders, clientSales, openOrders, paidOrders, openSales, paidSales, totalOpen, totalPaid };
  }, [selectedClient, orders, pieceSales]);

  // ─── Devedores: lista de todos os clientes com pagamento em aberto ────────
  const debtorsList = useMemo(() => {
    const map = new Map();
    const keyOf = (name) => (name || "—").trim().toLowerCase();
    orders
      .filter((o) => o.status !== "delivered" || o.payMethod === "anotar")
      .forEach((o) => {
        const k = keyOf(o.clientName);
        if (!k || k === "—") return;
        if (!map.has(k)) map.set(k, { name: o.clientName || "—", phone: o.clientPhone || "", id: o.clientId || `dbt-${k}`, openOrders: 0, openSales: 0, totalOpen: 0, lastDate: null });
        const g = map.get(k);
        g.openOrders += 1;
        g.totalOpen += Number(o.total) || 0;
        const d = parseBR(o.exitDate || o.entryDate, o.createdAt);
        if (d && (!g.lastDate || d > g.lastDate)) g.lastDate = d;
        if (!g.phone && o.clientPhone) g.phone = o.clientPhone;
      });
    pieceSales
      .filter((s) => !s.payMethod || s.payMethod === "anotar")
      .forEach((s) => {
        const k = keyOf(s.clientStore);
        if (!k || k === "—") return;
        if (!map.has(k)) map.set(k, { name: s.clientStore || "—", phone: "", id: `dbt-${k}`, openOrders: 0, openSales: 0, totalOpen: 0, lastDate: null });
        const g = map.get(k);
        g.openSales += 1;
        g.totalOpen += Number(s.value) || 0;
        const d = parseBR(s.date, s.createdAt);
        if (d && (!g.lastDate || d > g.lastDate)) g.lastDate = d;
      });
    return Array.from(map.values())
      .filter((g) => g.totalOpen > 0)
      .sort((a, b) => b.totalOpen - a.totalOpen);
  }, [orders, pieceSales]);

  return (
    <div className="p-6 space-y-4" data-testid="fechamento-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Fechamento</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {mode === "client" && "Pagamento pendente por cliente — selecione os serviços para cobrar"}
            {mode === "pending" && "Todos os recebíveis em aberto — filtre pela data de saída"}
          </p>
        </div>
        <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
          <button
            onClick={() => setMode("client")}
            data-testid="mode-client"
            className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
              mode === "client" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            Por cliente
          </button>
          <button
            onClick={() => setMode("pending")}
            data-testid="mode-pending"
            className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
              mode === "pending" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            Pendentes (geral)
          </button>
        </div>
      </div>

      {/* Global period filter (uses exitDate) */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center flex-wrap gap-3" data-testid="period-filter-bar">
        <span className="text-xs uppercase font-bold text-slate-500 tracking-wider">Filtrar por período (data de saída):</span>
        <div className="flex items-center gap-2">
          <label className="text-[11px] text-slate-500 font-semibold">De</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="period-from"
            className="h-8 rounded-md border border-slate-300 px-2 text-xs bg-white focus:border-blue-500 focus:outline-none" />
          <label className="text-[11px] text-slate-500 font-semibold">Até</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="period-to"
            className="h-8 rounded-md border border-slate-300 px-2 text-xs bg-white focus:border-blue-500 focus:outline-none" />
        </div>
        <button onClick={() => {
          const d = new Date();
          const first = new Date(d.getFullYear(), d.getMonth(), 1);
          const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
          const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
          setDateFrom(iso(first)); setDateTo(iso(last));
        }} data-testid="period-this-month"
          className="h-8 px-3 rounded-md text-[11px] font-semibold border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700">
          Este mês
        </button>
        <button onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="period-clear"
          className="h-8 px-3 rounded-md text-[11px] font-semibold border border-slate-200 text-slate-600 hover:border-rose-300 hover:text-rose-700">
          Limpar
        </button>
        {(dateFrom || dateTo) && (
          <span className="text-[11px] text-blue-700 font-semibold ml-auto">
            Filtrando: {dateFrom ? dateFrom.split("-").reverse().join("/") : "—"} → {dateTo ? dateTo.split("-").reverse().join("/") : "—"}
          </span>
        )}
      </div>

      {mode === "client" && (
        <>
          {/* Search + selected banner */}
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={clientQuery}
              onChange={(e) => {
                setClientQuery(e.target.value);
                setSelectedClient(null);
              }}
              placeholder="Buscar cliente pelo nome ou telefone..."
              data-testid="fech-client-search"
              className="w-full h-10 rounded-lg border border-slate-300 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none bg-white"
            />
            {clientMatches.length > 0 && !selectedClient && (
              <ul className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden max-h-72 overflow-y-auto">
                {clientMatches.map((c) => (
                  <li
                    key={c.id}
                    onClick={() => {
                      setSelectedClient(c);
                      setClientQuery(c.name);
                    }}
                    data-testid={`fech-client-pick-${c.id}`}
                    className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0"
                  >
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-slate-400">{c.phone}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {!selectedClient ? (
            debtorsList.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-12 text-center">
                <User className="w-10 h-10 mx-auto text-slate-300" />
                <p className="mt-3 text-sm text-slate-500">
                  Nenhum cliente com pagamento em aberto no momento. 🎉
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="debtors-list">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900 text-sm">Clientes com pagamento em aberto</h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {debtorsList.length} cliente(s) · Total a receber: <b className="text-rose-700">R$ {fmtBRL(debtorsList.reduce((s, d) => s + d.totalOpen, 0))}</b>
                    </p>
                  </div>
                  <span className="text-[11px] text-slate-400 italic">Clique em um cliente para abrir o fechamento</span>
                </div>
                <div className="max-h-[520px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0" style={{ background: HEADER_BG }}>
                      <tr>
                        {["CLIENTE", "TELEFONE", "Nº OS", "PEÇAS", "ÚLTIMO ITEM", "A RECEBER"].map((h) => (
                          <th key={h} className="text-left text-[10px] font-bold uppercase tracking-wider text-white px-3 py-2 whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {debtorsList.map((d) => (
                        <tr key={d.id}
                          onClick={() => { setSelectedClient({ id: d.id, name: d.name, phone: d.phone }); setClientQuery(d.name); }}
                          data-testid={`debtor-row-${d.name}`}
                          className="border-t border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors">
                          <td className="px-3 py-2 text-xs font-semibold text-slate-800">{d.name}</td>
                          <td className="px-3 py-2 text-xs text-slate-500 tabular-nums whitespace-nowrap">{d.phone || <span className="italic text-slate-300">—</span>}</td>
                          <td className="px-3 py-2 text-xs text-slate-700 tabular-nums">{d.openOrders}</td>
                          <td className="px-3 py-2 text-xs text-slate-700 tabular-nums">{d.openSales}</td>
                          <td className="px-3 py-2 text-xs text-slate-500 tabular-nums whitespace-nowrap">
                            {d.lastDate ? `${String(d.lastDate.getDate()).padStart(2,"0")}/${String(d.lastDate.getMonth()+1).padStart(2,"0")}/${d.lastDate.getFullYear()}` : "—"}
                          </td>
                          <td className="px-3 py-2 text-xs font-bold text-rose-700 tabular-nums whitespace-nowrap">R$ {fmtBRL(d.totalOpen)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          ) : (
            <ClientClosure client={selectedClient} data={clientData} onRefresh={onRefresh} dateFrom={dateFrom} dateTo={dateTo}
              onOpenCobranca={(payload) => setCobranca(payload)} />
          )}
        </>
      )}

      {mode === "pending" && (
        <PendingGlobalView
          orders={orders}
          pieceSales={pieceSales}
          dateFrom={dateFrom}
          dateTo={dateTo}
          store={store}
          onOpenClient={(c) => {
            setSelectedClient(c);
            setClientQuery(c.name);
            setMode("client");
          }}
          onOpenCobranca={(payload) => setCobranca(payload)}
          onRefresh={onRefresh}
        />
      )}

      {cobranca && (
        <CobrancaModal
          client={cobranca.client}
          rows={cobranca.rows}
          period={cobranca.period}
          store={store}
          onClose={() => setCobranca(null)}
          onCloseThenSettle={({ rows, discount, subtotal, client }) => {
            // Only rows that carry an id can be paid (multi-client batch has ids too).
            const payable = rows.filter((r) => !!r.id).map((r) => ({ kind: r.kind, id: r.id, value: r.value }));
            if (payable.length === 0) return;
            setCobranca(null);
            setPayFromCobranca({ rows: payable, total: subtotal, client, initialDiscount: discount });
          }}
        />
      )}

      {payFromCobranca && (
        <PayModal
          rows={payFromCobranca.rows}
          total={payFromCobranca.total}
          client={payFromCobranca.client}
          initialDiscount={payFromCobranca.initialDiscount}
          onClose={() => setPayFromCobranca(null)}
          onDone={() => { setPayFromCobranca(null); onRefresh && onRefresh(); }}
        />
      )}
    </div>
  );
}

function ClientClosure({ client, data, onRefresh, dateFrom, dateTo, onOpenCobranca }) {
  const [payFor, setPayFor] = useState(null); // { rows: [...], total, kind: "all"|"single"|"selected" }
  const [selected, setSelected] = useState(new Set());

  // Self-correct selection: drop keys that no longer exist (e.g. after paying selected items)
  const openOrdersCount = data?.openOrders?.length || 0;
  const openSalesCount = data?.openSales?.length || 0;
  useEffect(() => {
    const validKeys = new Set([
      ...(data?.openOrders || []).map((o) => `order-${o.id}`),
      ...(data?.openSales || []).map((s) => `sale-${s.id}`),
    ]);
    setSelected((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((k) => { if (validKeys.has(k)) next.add(k); else changed = true; });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOrdersCount, openSalesCount]);

  if (!data) return null;

  // Range filter based on data de saída (o.exitDate) — piece sales use s.date.
  // Non-delivered items without exitDate: only pass when there's NO active filter.
  const from = fromISOInput(dateFrom);
  const to = fromISOInput(dateTo);
  if (to) to.setHours(23, 59, 59, 999);
  const rangeActive = !!(from || to);

  const withinRange = (br) => {
    if (!rangeActive) return true;
    const d = parseBR(br, null);
    if (!d) return false;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  };

  const openOrdersFiltered = data.openOrders.filter((o) => withinRange(o.exitDate));
  const openSalesFiltered = data.openSales.filter((s) => withinRange(s.date));

  const openRowsFiltered = [
    ...openOrdersFiltered.map((o) => ({
      kind: "order",
      id: o.id,
      orderNumber: o.orderNumber || "",
      imei: o.imei || "",
      date: o.exitDate || o.entryDate,
      description: `${o.model || ""}${(o.services && o.services.length) ? " — " + o.services.join(", ") : ""}`,
      items: o.services || [],
      value: Number(o.total) || 0,
    })),
    ...openSalesFiltered.map((s) => ({
      kind: "sale",
      id: s.id,
      orderNumber: "",
      imei: "",
      date: s.date,
      description: s.piece || "",
      items: [],
      value: Number(s.value) || 0,
    })),
  ];
  const openTotalFiltered = openRowsFiltered.reduce((s, r) => s + r.value, 0);
  const selectedRows = openRowsFiltered.filter((r) => selected.has(`${r.kind}-${r.id}`));
  const selectedTotal = selectedRows.reduce((s, r) => s + r.value, 0);

  const toggleSel = (r) => {
    const key = `${r.kind}-${r.id}`;
    setSelected((prev) => { const s = new Set(prev); if (s.has(key)) s.delete(key); else s.add(key); return s; });
  };
  const toggleAllVisible = () => {
    const keys = openRowsFiltered.map((r) => `${r.kind}-${r.id}`);
    const allSel = keys.length > 0 && keys.every((k) => selected.has(k));
    setSelected((prev) => { const s = new Set(prev); keys.forEach((k) => (allSel ? s.delete(k) : s.add(k))); return s; });
  };

  const buildCobrancaRows = (items) => items.map((r) => ({
    kind: r.kind,
    id: r.id,
    orderNumber: r.orderNumber,
    imei: r.imei || "",
    date: r.date,
    description: r.description,
    items: r.items,
    value: r.value,
  }));

  const emitCobranca = () => {
    const items = selectedRows.length > 0 ? selectedRows : openRowsFiltered;
    if (items.length === 0) return;
    const brFrom = dateFrom ? dateFrom.split("-").reverse().join("/") : "";
    const brTo = dateTo ? dateTo.split("-").reverse().join("/") : "";
    onOpenCobranca({
      client: { name: client.name, phone: client.phone },
      rows: buildCobrancaRows(items),
      period: { from: brFrom, to: brTo },
    });
  };

  return (
    <div className="space-y-4">
      {/* Selection + cobrança bar */}
      {openRowsFiltered.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center flex-wrap gap-3" data-testid="client-selection-bar">
          <button
            onClick={toggleAllVisible}
            data-testid="toggle-all-visible"
            className="h-8 px-3 rounded-md text-[11px] font-semibold border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700"
          >
            {openRowsFiltered.every((r) => selected.has(`${r.kind}-${r.id}`)) ? "Desmarcar tudo" : "Selecionar tudo"}
          </button>
          <span className="text-xs text-slate-500 tabular-nums">
            {openRowsFiltered.length} item(s) visíveis · <b>R$ {fmtBRL(openTotalFiltered)}</b>
          </span>
          {selected.size > 0 && (
            <span className="text-xs text-blue-700 font-semibold tabular-nums">
              · {selected.size} selecionado(s) · R$ {fmtBRL(selectedTotal)}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={emitCobranca}
              data-testid="cobranca-pdf-button"
              disabled={openRowsFiltered.length === 0}
              className="h-9 px-3 rounded-md text-xs font-semibold bg-white border-2 border-blue-600 text-blue-700 hover:bg-blue-600 hover:text-white inline-flex items-center gap-1.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              title={selected.size > 0 ? "Gerar PDF apenas com os itens selecionados" : "Gerar PDF com todos os itens visíveis"}
            >
              <Printer className="w-3.5 h-3.5" />
              PDF cobrança {selected.size > 0 ? `(${selected.size})` : "(todos visíveis)"}
            </button>
            {selected.size > 0 && (
              <button
                onClick={() => setPayFor({ rows: selectedRows, total: selectedTotal, kind: "selected" })}
                data-testid="close-selected-button"
                className="h-9 px-3 rounded-md text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1.5"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Fechar {selected.size} — R$ {fmtBRL(selectedTotal)}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-slate-500">
          Fechamento de <b className="text-slate-800">{client.name}</b>
          {client.phone ? <span className="text-slate-400"> · {client.phone}</span> : null}
          {rangeActive && (
            <span className="ml-2 text-blue-700 font-semibold">
              · período (saída): {dateFrom ? dateFrom.split("-").reverse().join("/") : "—"} a {dateTo ? dateTo.split("-").reverse().join("/") : "—"}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => exportClientClosureCSV(client, data)}
            data-testid="export-csv"
            disabled={data.totalOpen === 0 && data.totalPaid === 0}
            className="h-9 px-3 rounded-lg text-xs font-semibold bg-white border border-slate-200 text-slate-700 hover:border-emerald-400 hover:text-emerald-700 inline-flex items-center gap-1.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            title="Exportar planilha CSV (abre no Excel)"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
          </button>
          <button
            onClick={() => printClientClosure(client, data)}
            data-testid="export-pdf"
            disabled={data.totalOpen === 0 && data.totalPaid === 0}
            className="h-9 px-3 rounded-lg text-xs font-semibold bg-white border border-slate-200 text-slate-700 hover:border-blue-400 hover:text-blue-700 inline-flex items-center gap-1.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            title="Relatório geral do cliente (todos itens)"
          >
            <Printer className="w-3.5 h-3.5" /> Relatório
          </button>
          <a
            href={buildWhatsAppLink(client, data)}
            target="_blank"
            rel="noreferrer"
            data-testid="wa-receipt"
            className={`h-9 px-3 rounded-lg text-xs font-semibold border inline-flex items-center gap-1.5 transition-all ${
              data.totalOpen === 0 && data.totalPaid === 0
                ? "pointer-events-none opacity-40 border-slate-200 text-slate-400 bg-white"
                : "border-emerald-200 text-emerald-700 bg-white hover:bg-emerald-600 hover:text-white hover:border-emerald-600"
            }`}
            title={data.totalOpen > 0 ? "Enviar cobrança por WhatsApp" : "Enviar recibo por WhatsApp"}
          >
            <MessageCircle className="w-3.5 h-3.5" />
            {data.totalOpen > 0 ? "Cobrar" : "Recibo"}
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase font-bold text-rose-600 tracking-wider">A receber {rangeActive ? "(no período)" : "(total)"}</div>
              <div className="font-heading text-4xl font-extrabold text-rose-800 tabular-nums mt-1">
                R$ {fmtBRL(rangeActive ? openTotalFiltered : data.totalOpen)}
              </div>
              <div className="text-xs text-rose-600 mt-1">
                {rangeActive
                  ? `${openOrdersFiltered.length} OS + ${openSalesFiltered.length} vendas`
                  : `${data.openOrders.length} OS + ${data.openSales.length} vendas em aberto`}
              </div>
            </div>
            {openRowsFiltered.length > 0 && (
              <button
                onClick={() => setPayFor({ rows: openRowsFiltered, total: openTotalFiltered, kind: "all" })}
                data-testid="close-all-button"
                className="h-10 px-4 rounded-lg text-sm font-semibold bg-rose-600 hover:bg-rose-700 text-white inline-flex items-center gap-1.5 transition-all hover:-translate-y-[1px] hover:shadow-md whitespace-nowrap"
              >
                <CheckCircle2 className="w-4 h-4" /> Fechar tudo {rangeActive ? "(visível)" : ""}
              </button>
            )}
          </div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
          <div className="text-xs uppercase font-bold text-emerald-600 tracking-wider">Já pago</div>
          <div className="font-heading text-4xl font-extrabold text-emerald-800 tabular-nums mt-1">
            R$ {fmtBRL(data.totalPaid)}
          </div>
          <div className="text-xs text-emerald-600 mt-1">
            {data.paidOrders.length} OS + {data.paidSales.length} vendas quitadas
          </div>
        </div>
      </div>

      <OrderRowsTable
        title={`OS em aberto de ${client.name}${rangeActive ? " (no período)" : ""}`}
        orders={openOrdersFiltered}
        emptyMsg="Nenhuma OS em aberto no período."
        variant="open"
        selected={selected}
        onToggleSelect={(o) => toggleSel({ kind: "order", id: o.id })}
        onPaySingle={(o) => setPayFor({ rows: [{ kind: "order", id: o.id, value: Number(o.total) || 0 }], total: Number(o.total) || 0, kind: "single" })}
      />
      <PieceRowsTable
        title={`Vendas de peças em aberto de ${client.name}${rangeActive ? " (no período)" : ""}`}
        sales={openSalesFiltered}
        emptyMsg="Nenhuma venda em aberto no período."
        variant="open"
        selected={selected}
        onToggleSelect={(s) => toggleSel({ kind: "sale", id: s.id })}
        onPaySingle={(s) => setPayFor({ rows: [{ kind: "sale", id: s.id, value: Number(s.value) || 0 }], total: Number(s.value) || 0, kind: "single" })}
      />

      <OrderRowsTable
        title={`OS já pagas de ${client.name}`}
        orders={data.paidOrders}
        emptyMsg="Nenhuma OS paga registrada."
        variant="paid"
      />
      <PieceRowsTable
        title={`Vendas de peças pagas de ${client.name}`}
        sales={data.paidSales}
        emptyMsg="Nenhuma venda paga registrada."
        variant="paid"
      />

      {payFor && (
        <PayModal
          rows={payFor.rows}
          total={payFor.total}
          client={client}
          onClose={() => setPayFor(null)}
          onDone={() => {
            setSelected(new Set());
            setPayFor(null);
            onRefresh && onRefresh();
          }}
        />
      )}
    </div>
  );
}

function buildWhatsAppLink(client, data) {
  const nf = (v) => `R$ ${fmtBRL(v)}`;
  if (data.totalOpen > 0) {
    const openLines = [
      ...data.openOrders.map((o) => `• ${o.orderNumber || "OS"} — ${o.model || ""} — ${nf(o.total)}`),
      ...data.openSales.map((s) => `• ${s.piece} — ${nf(s.value)}`),
    ].join("\n");
    const msg =
      `Olá ${client.name}! 👋\n\n` +
      `Passando o fechamento com os itens em aberto na Phone Lab:\n\n` +
      `${openLines}\n\n` +
      `*Total: ${nf(data.totalOpen)}*\n\n` +
      `Quando puder passar o pagamento, é só me avisar. Obrigado! 🙏`;
    return waLink(client.phone, msg);
  }
  const paidLines = [
    ...data.paidOrders.map((o) => `• ${o.orderNumber || "OS"} — ${o.model || ""} — ${nf(o.total)} (${PAY_LABELS[o.payMethod] || "—"})`),
    ...data.paidSales.map((s) => `• ${s.piece} — ${nf(s.value)} (${PAY_LABELS[s.payMethod] || "—"})`),
  ].join("\n");
  const msg =
    `Olá ${client.name}! ✅\n\n` +
    `Recibo dos pagamentos registrados na Phone Lab:\n\n` +
    `${paidLines}\n\n` +
    `*Total pago: ${nf(data.totalPaid)}*\n\n` +
    `Obrigado pela confiança! 🤝`;
  return waLink(client.phone, msg);
}

// ─── OS table (colunas iguais à Saída, sem custo e sem obs) ────────────────
function OrderRowsTable({ title, orders, emptyMsg, variant, onPaySingle, selected, onToggleSelect }) {
  // 'paid' variant: subtotal is net of discount (matches Financeiro/já-pago card)
  const total = orders.reduce((s, o) => {
    const gross = Number(o.total) || 0;
    const disc = variant === "paid" ? (Number(o.discount) || 0) : 0;
    return s + Math.max(0, gross - disc);
  }, 0);
  const accent = variant === "open" ? "text-rose-700" : "text-emerald-700";
  const selectable = variant === "open" && !!onToggleSelect;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-heading font-semibold text-slate-800 text-sm">{title}</h3>
        <div className={`font-heading font-bold tabular-nums ${accent}`}>R$ {fmtBRL(total)}</div>
      </div>
      {orders.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-slate-400 italic">{emptyMsg}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: HEADER_BG }}>
                {selectable && (
                  <th className="w-10 px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-white">
                    <input
                      type="checkbox"
                      checked={orders.length > 0 && orders.every((o) => selected.has(`order-${o.id}`))}
                      onChange={() => {
                        const allSel = orders.every((o) => selected.has(`order-${o.id}`));
                        orders.forEach((o) => {
                          const isSel = selected.has(`order-${o.id}`);
                          if (allSel && isSel) onToggleSelect(o); // deselect all
                          else if (!allSel && !isSel) onToggleSelect(o); // select missing
                        });
                      }}
                      data-testid="select-all-orders"
                      className="w-3.5 h-3.5 cursor-pointer accent-blue-600"
                    />
                  </th>
                )}
                {["OS / IMEI", "DATA ENT.", "HORA ENT.", "DATA SAÍ.", "HORA SAÍ.", "CLIENTE", "APARELHO", "SERVIÇO", "VALOR (R$)", "PAGAMENTO"].map((h) => (
                  <th
                    key={h}
                    className="text-left text-[10px] font-bold uppercase tracking-wider text-white px-3 py-2 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
                {variant === "open" && <th className="w-24"></th>}
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const pc = o.payMethod ? PAY_COLORS[o.payMethod] : null;
                const isSel = selectable && selected.has(`order-${o.id}`);
                return (
                  <tr key={o.id} className={`border-t border-slate-100 hover:bg-blue-50/30 ${isSel ? "bg-blue-50" : ""}`}>
                    {selectable && (
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => onToggleSelect(o)}
                          data-testid={`select-order-${o.id}`}
                          className="w-4 h-4 cursor-pointer accent-blue-600"
                        />
                      </td>
                    )}
                    <td className="px-3 py-2 whitespace-nowrap">
                      {o.orderNumber ? (
                        <div className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 border border-blue-100 text-blue-700 inline-block">
                          {o.orderNumber}
                        </div>
                      ) : (
                        <span className="text-[10px] italic text-slate-300">—</span>
                      )}
                      {o.imei && (
                        <div className="text-[10px] text-slate-500 tabular-nums mt-0.5" title="IMEI ou OS">{o.imei}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">{o.entryDate || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 tabular-nums whitespace-nowrap">{o.entryTime || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">{o.exitDate || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 tabular-nums whitespace-nowrap">{o.exitTime || "—"}</td>
                    <td className="px-3 py-2">
                      <div className="text-xs font-semibold text-slate-800">{o.clientName}</div>
                      <div className="text-[10px] text-slate-400">{o.clientPhone}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-xs text-slate-700">{o.model}</div>
                      <div className="text-[10px] text-slate-400">{o.color}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(o.services || []).map((s) => (
                          <span
                            key={s}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 border border-blue-100 text-blue-700 whitespace-nowrap"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs font-semibold text-slate-800 tabular-nums whitespace-nowrap">
                      R$ {fmtBRL(o.total)}
                    </td>
                    <td className="px-3 py-2">
                      {pc ? (
                        <span
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap"
                          style={{ background: pc.bg, color: pc.color, borderColor: pc.border }}
                        >
                          {PAY_LABELS[o.payMethod]}
                        </span>
                      ) : (
                        <span className="text-[10px] italic text-slate-300">—</span>
                      )}
                    </td>
                    {variant === "open" && (
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => onPaySingle && onPaySingle(o)}
                          data-testid={`pay-order-${o.id}`}
                          className="h-7 px-2.5 rounded-md text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all inline-flex items-center gap-1"
                        >
                          <CheckCircle2 className="w-3 h-3" /> Pago
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Piece Sales table (todos os dados) ────────────────────────────────────
function PieceRowsTable({ title, sales, emptyMsg, variant, onPaySingle, selected, onToggleSelect }) {
  const total = sales.reduce((s, x) => {
    const gross = Number(x.value) || 0;
    const disc = variant === "paid" ? (Number(x.discount) || 0) : 0;
    return s + Math.max(0, gross - disc);
  }, 0);
  const accent = variant === "open" ? "text-rose-700" : "text-emerald-700";
  const selectable = variant === "open" && !!onToggleSelect;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-heading font-semibold text-slate-800 text-sm">{title}</h3>
        <div className={`font-heading font-bold tabular-nums ${accent}`}>R$ {fmtBRL(total)}</div>
      </div>
      {sales.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-slate-400 italic">{emptyMsg}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: HEADER_BG }}>
                {selectable && (
                  <th className="w-10 px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-white">
                    <input
                      type="checkbox"
                      checked={sales.length > 0 && sales.every((s) => selected.has(`sale-${s.id}`))}
                      onChange={() => {
                        const allSel = sales.every((s) => selected.has(`sale-${s.id}`));
                        sales.forEach((s) => {
                          const isSel = selected.has(`sale-${s.id}`);
                          if (allSel && isSel) onToggleSelect(s);
                          else if (!allSel && !isSel) onToggleSelect(s);
                        });
                      }}
                      data-testid="select-all-sales"
                      className="w-3.5 h-3.5 cursor-pointer accent-blue-600"
                    />
                  </th>
                )}
                {["DATA", "HORA", "CLIENTE/LOJA", "PEÇA", "VALOR (R$)", "PAGAMENTO", "OBS."].map((h) => (
                  <th
                    key={h}
                    className="text-left text-[10px] font-bold uppercase tracking-wider text-white px-3 py-2 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
                {variant === "open" && <th className="w-24"></th>}
              </tr>
            </thead>
            <tbody>
              {sales.map((s) => {
                const pc = s.payMethod ? PAY_COLORS[s.payMethod] : null;
                const isSel = selectable && selected.has(`sale-${s.id}`);
                return (
                  <tr key={s.id} className={`border-t border-slate-100 hover:bg-blue-50/30 ${isSel ? "bg-blue-50" : ""}`}>
                    {selectable && (
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => onToggleSelect(s)}
                          data-testid={`select-sale-${s.id}`}
                          className="w-4 h-4 cursor-pointer accent-blue-600"
                        />
                      </td>
                    )}
                    <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">{s.date}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 tabular-nums whitespace-nowrap">{s.time}</td>
                    <td className="px-3 py-2 text-xs font-semibold text-slate-800">{s.clientStore}</td>
                    <td className="px-3 py-2 text-xs text-slate-700">{s.piece}</td>
                    <td className="px-3 py-2 text-xs font-semibold text-slate-800 tabular-nums whitespace-nowrap">
                      R$ {fmtBRL(s.value)}
                    </td>
                    <td className="px-3 py-2">
                      {pc ? (
                        <span
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap"
                          style={{ background: pc.bg, color: pc.color, borderColor: pc.border }}
                        >
                          {PAY_LABELS[s.payMethod]}
                        </span>
                      ) : (
                        <span className="text-[10px] italic text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-600 max-w-[180px] truncate" title={s.observation}>
                      {s.observation || <span className="italic text-slate-300">—</span>}
                    </td>
                    {variant === "open" && (
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => onPaySingle && onPaySingle(s)}
                          data-testid={`pay-sale-${s.id}`}
                          className="h-7 px-2.5 rounded-md text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all inline-flex items-center gap-1"
                        >
                          <CheckCircle2 className="w-3 h-3" /> Pago
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const PAY_OPTIONS_MODAL = [
  { key: "pix_loja", label: "Pix Loja" },
  { key: "pix_kevin", label: "Pix Kevin" },
  { key: "dinheiro", label: "Dinheiro" },
  { key: "cartao", label: "Cartão" },
  { key: "devolvido", label: "Devolvido" },
];

function PayModal({ rows, total, client, onClose, onDone, initialDiscount = 0 }) {
  const [pay, setPay] = useState("pix_loja");
  const [desc, setDesc] = useState("");
  const [discountStr, setDiscountStr] = useState(initialDiscount > 0 ? initialDiscount.toFixed(2) : "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const discount = Math.max(0, Math.min(total, parseFloat((discountStr || "").replace(",", ".")) || 0));
  const finalTotal = total - discount;

  const confirm = async () => {
    setSaving(true);
    setErr("");
    try {
      // Distribute discount proportionally across rows
      const totalOriginal = rows.reduce((s, r) => s + (Number(r.value) || 0), 0) || 1;
      await Promise.all(
        rows.map((r) => {
          const share = discount > 0 ? +((r.value / totalOriginal) * discount).toFixed(2) : 0;
          const body = { payMethod: pay, cartaoDesc: desc || "", discount: share };
          return r.kind === "order"
            ? api.post(`/orders/${r.id}/pay`, body)
            : api.post(`/piece-sales/${r.id}/pay`, body);
        })
      );
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || "Erro ao marcar como pago");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-emerald-600 to-emerald-800 text-white flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold">Confirmar pagamento</h3>
            <p className="text-xs text-white/80 mt-0.5">
              {client.name} · {rows.length} {rows.length === 1 ? "item" : "itens"}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-1">
            {discount > 0 && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 uppercase font-semibold tracking-wider">Original</span>
                <span className="tabular-nums text-slate-600 line-through">R$ {fmtBRL(total)}</span>
              </div>
            )}
            {discount > 0 && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-rose-600 uppercase font-semibold tracking-wider">Desconto</span>
                <span className="tabular-nums text-rose-700 font-semibold">- R$ {fmtBRL(discount)}</span>
              </div>
            )}
            <div className={discount > 0 ? "pt-1 border-t border-emerald-200" : ""}>
              <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Total a receber</div>
              <div className="font-heading text-4xl font-extrabold text-emerald-900 tabular-nums" data-testid="pay-final-total">
                R$ {fmtBRL(finalTotal)}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">
              Desconto (opcional)
            </label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold">R$</span>
              <input
                inputMode="decimal"
                value={discountStr}
                onChange={(e) => setDiscountStr(e.target.value)}
                placeholder="0,00"
                data-testid="pay-discount"
                className="w-full h-10 rounded-lg border border-slate-300 pl-10 pr-3 text-sm focus:border-emerald-500 focus:outline-none text-right tabular-nums"
              />
            </div>
            <div className="flex gap-1.5 mt-2">
              {[5, 10, 20, 50].map((pct) => (
                <button
                  key={pct}
                  type="button"
                  onClick={() => setDiscountStr(((total * pct) / 100).toFixed(2))}
                  data-testid={`pay-discount-${pct}`}
                  className="h-7 px-2 rounded text-[10px] font-semibold bg-white border border-slate-200 text-slate-600 hover:border-emerald-400 hover:text-emerald-700"
                >
                  -{pct}%
                </button>
              ))}
              {discountStr && (
                <button
                  type="button"
                  onClick={() => setDiscountStr("")}
                  className="h-7 px-2 rounded text-[10px] font-semibold bg-white border border-slate-200 text-slate-500 hover:border-rose-300 hover:text-rose-600"
                >
                  Limpar
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">
              Forma de pagamento
            </label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {PAY_OPTIONS_MODAL.map((o) => (
                <button
                  key={o.key}
                  onClick={() => setPay(o.key)}
                  data-testid={`paymodal-${o.key}`}
                  className={`h-10 rounded-lg text-sm font-semibold transition-all border ${
                    pay === o.key
                      ? "bg-emerald-600 border-emerald-600 text-white shadow-sm"
                      : "bg-white border-slate-200 text-slate-700 hover:border-emerald-300"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {pay === "cartao" && (
            <div>
              <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                Descrição do cartão
              </label>
              <input
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="Ex.: Débito Visa, Crédito 3x…"
                className="mt-1.5 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>
          )}

          {err && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200"
          >
            Cancelar
          </button>
          <button
            onClick={confirm}
            disabled={saving}
            data-testid="confirm-payment"
            className="h-10 px-5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Confirmar pagamento
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Pending Global View (all open receivables across months, grouped) ─────
function PendingGlobalView({ orders, pieceSales, dateFrom, dateTo, onOpenClient, onRefresh, onOpenCobranca, store }) {
  const [filter, setFilter] = useState("all"); // "all" | "overdue"
  const [payFor, setPayFor] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const from = fromISOInput(dateFrom);
  const to = fromISOInput(dateTo);
  if (to) to.setHours(23, 59, 59, 999);
  const rangeActive = !!(from || to);

  const rows = useMemo(() => {
    const openOrders = orders
      .filter((o) => (o.status !== "delivered") || o.payMethod === "anotar")
      .map((o) => {
        // Reference date = data de SAÍDA (fallback entryDate for overdue calc when no exit yet)
        const exitRef = parseBR(o.exitDate, null);
        const overdueRef = exitRef || parseBR(o.entryDate, o.createdAt);
        const info = overdueInfo(overdueRef);
        return {
          kind: "order",
          id: o.id,
          orderNumber: o.orderNumber,
          imei: o.imei || "",
          clientName: o.clientName || "—",
          clientPhone: o.clientPhone || "",
          clientId: o.clientId,
          desc: `${o.model || ""}${(o.services && o.services.length) ? " — " + o.services.join(", ") : ""}`,
          items: o.services || [],
          entryDate: o.entryDate || "",
          exitDate: o.exitDate || "",
          exitRef, // Date | null (used by range filter)
          value: Number(o.total) || 0,
          refDate: overdueRef,
          ...info,
        };
      });

    const openSales = pieceSales
      .filter((s) => !s.payMethod || s.payMethod === "anotar")
      .map((s) => {
        const exitRef = parseBR(s.date, s.createdAt);
        const info = overdueInfo(exitRef);
        return {
          kind: "sale",
          id: s.id,
          orderNumber: "",
          clientName: s.clientStore || "—",
          clientPhone: "",
          desc: s.piece || "",
          items: [],
          entryDate: s.date || "",
          exitDate: s.date || "",
          exitRef,
          value: Number(s.value) || 0,
          refDate: exitRef,
          ...info,
        };
      });

    const all = [...openOrders, ...openSales];
    all.sort((a, b) => {
      if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
      if (a.overdue && b.overdue) return b.daysLate - a.daysLate;
      const at = a.refDate ? a.refDate.getTime() : 0;
      const bt = b.refDate ? b.refDate.getTime() : 0;
      return at - bt;
    });
    return all;
  }, [orders, pieceSales]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filter === "overdue" && !r.overdue) return false;
      if (rangeActive) {
        if (!r.exitRef) return false;
        if (from && r.exitRef < from) return false;
        if (to && r.exitRef > to) return false;
      }
      return true;
    });
  }, [rows, filter, rangeActive, from, to]);

  // Self-correct selection: drop keys not in the current filtered row set
  useEffect(() => {
    const validKeys = new Set(filteredRows.map((r) => `${r.kind}-${r.id}`));
    setSelected((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((k) => { if (validKeys.has(k)) next.add(k); else changed = true; });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of filteredRows) {
      const key = (r.clientName || "—").toLowerCase();
      if (!map.has(key)) {
        map.set(key, { name: r.clientName, phone: r.clientPhone, items: [], total: 0, overdueCount: 0, maxDaysLate: 0 });
      }
      const g = map.get(key);
      g.items.push(r);
      g.total += r.value;
      if (r.overdue) { g.overdueCount += 1; g.maxDaysLate = Math.max(g.maxDaysLate, r.daysLate); }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.overdueCount !== b.overdueCount) return b.overdueCount - a.overdueCount;
      return b.total - a.total;
    });
  }, [filteredRows]);

  const totals = useMemo(() => {
    const overdueRows = filteredRows.filter((r) => r.overdue);
    return {
      count: filteredRows.length,
      total: filteredRows.reduce((s, r) => s + r.value, 0),
      overdueCount: overdueRows.length,
      overdueTotal: overdueRows.reduce((s, r) => s + r.value, 0),
    };
  }, [filteredRows]);

  const selectedRows = filteredRows.filter((r) => selected.has(`${r.kind}-${r.id}`));
  const selectedTotal = selectedRows.reduce((s, r) => s + r.value, 0);

  const toggleSel = (r) => {
    const key = `${r.kind}-${r.id}`;
    setSelected((prev) => { const s = new Set(prev); if (s.has(key)) s.delete(key); else s.add(key); return s; });
  };

  const emitCobrancaGrouped = () => {
    // If selection is empty, use all visible rows. If all rows belong to a single client, open the discount modal;
    // otherwise fall back to batch PDF generation (one per client, no discount).
    const useRows = selectedRows.length > 0 ? selectedRows : filteredRows;
    if (useRows.length === 0) return;
    const byClient = new Map();
    for (const r of useRows) {
      const key = (r.clientName || "—").toLowerCase();
      if (!byClient.has(key)) byClient.set(key, { name: r.clientName, phone: r.clientPhone, rows: [] });
      byClient.get(key).rows.push({
        kind: r.kind,
        id: r.id,
        orderNumber: r.orderNumber || "",
        imei: r.imei || "",
        date: r.exitDate || r.entryDate || "",
        description: r.desc || "",
        items: r.items || [],
        value: r.value,
      });
    }
    const brFrom = dateFrom ? dateFrom.split("-").reverse().join("/") : "";
    const brTo = dateTo ? dateTo.split("-").reverse().join("/") : "";
    if (byClient.size === 1) {
      const [only] = Array.from(byClient.values());
      onOpenCobranca({
        client: { name: only.name, phone: only.phone },
        rows: only.rows,
        period: { from: brFrom, to: brTo },
      });
      return;
    }
    // Multi-client → batch PDFs (no discount modal)
    for (const [, c] of byClient) {
      printCobrancaPDF({
        client: { name: c.name, phone: c.phone },
        rows: c.rows,
        period: { from: brFrom, to: brTo },
        store,
      });
    }
  };

  return (
    <div className="space-y-4" data-testid="pending-global-view">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="text-xs uppercase font-semibold text-slate-400 tracking-wider">Itens em aberto{rangeActive ? " (período)" : ""}</div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-1 tabular-nums">{totals.count}</div>
          <div className="text-sm text-slate-500 mt-0.5 tabular-nums">R$ {fmtBRL(totals.total)}</div>
        </div>
        <div className="bg-white rounded-xl border border-rose-200 p-5 shadow-sm">
          <div className="text-xs uppercase font-semibold text-rose-500 tracking-wider">Atrasados</div>
          <div className="font-heading text-3xl font-bold text-rose-700 mt-1 tabular-nums" data-testid="overdue-count">
            {totals.overdueCount}
          </div>
          <div className="text-sm text-rose-500 mt-0.5 tabular-nums">R$ {fmtBRL(totals.overdueTotal)}</div>
        </div>
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-5 shadow-sm text-white">
          <div className="text-xs uppercase font-semibold text-white/70 tracking-wider">Total a receber</div>
          <div className="font-heading text-3xl font-bold mt-1 tabular-nums">R$ {fmtBRL(totals.total)}</div>
          <div className="text-[11px] text-white/70 mt-1">Ref.: data de saída · atraso 30d após fechamento do mês</div>
        </div>
      </div>

      {/* Filter tabs + cobrança button */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
          <button
            onClick={() => setFilter("all")}
            data-testid="pending-filter-all"
            className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
              filter === "all" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setFilter("overdue")}
            data-testid="pending-filter-overdue"
            className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
              filter === "overdue" ? "bg-white text-rose-700 shadow-sm" : "text-slate-500"
            }`}
          >
            Apenas atrasados
            {totals.overdueCount > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold">
                {totals.overdueCount}
              </span>
            )}
          </button>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <span className="text-xs text-blue-700 font-semibold tabular-nums">
              {selected.size} selecionado(s) · R$ {fmtBRL(selectedTotal)}
            </span>
          )}
          <button
            onClick={emitCobrancaGrouped}
            data-testid="pending-cobranca-pdf"
            disabled={filteredRows.length === 0}
            className="h-9 px-3 rounded-md text-xs font-semibold bg-white border-2 border-blue-600 text-blue-700 hover:bg-blue-600 hover:text-white inline-flex items-center gap-1.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            title={selected.size > 0 ? "Gerar PDF de cobrança apenas com itens selecionados (um PDF por cliente)" : "Gerar PDF de cobrança com todos os itens visíveis (um PDF por cliente)"}
          >
            <Printer className="w-3.5 h-3.5" />
            PDF cobrança {selected.size > 0 ? `(${selected.size})` : "(todos visíveis)"}
          </button>
          {selected.size > 0 && (
            <button
              onClick={() => {
                // Group selected by client — open a single Pay modal per client only makes sense; for simplicity, if all selected belong to one client, open the modal; else warn.
                const clientsSet = new Set(selectedRows.map((r) => r.clientName));
                if (clientsSet.size > 1) {
                  alert("Selecione itens de apenas um cliente por vez para fechar. (Para cobrar de vários clientes de uma vez use o botão PDF cobrança.)");
                  return;
                }
                const first = selectedRows[0];
                setPayFor({
                  rows: selectedRows.map((r) => ({ kind: r.kind, id: r.id, value: r.value })),
                  total: selectedTotal,
                  client: { name: first.clientName, phone: first.clientPhone },
                });
              }}
              data-testid="pending-close-selected"
              className="h-9 px-3 rounded-md text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1.5"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Fechar selecionados
            </button>
          )}
        </div>
      </div>

      {/* Grouped list */}
      {grouped.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-12 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300" />
          <p className="mt-3 text-sm text-slate-500">
            {filter === "overdue" ? "Nenhum recebível atrasado. Bom trabalho!" : rangeActive ? "Nenhum recebível com data de saída no período selecionado." : "Nenhum recebível em aberto."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map((g) => (
            <PendingClientGroup
              key={g.name}
              group={g}
              selected={selected}
              onToggleSelect={toggleSel}
              onOpenClient={onOpenClient}
              onPaySingle={(row) =>
                setPayFor({
                  rows: [{ kind: row.kind, id: row.id, value: row.value }],
                  total: row.value,
                  client: { name: g.name, phone: g.phone },
                })
              }
              onPayAllForClient={() =>
                setPayFor({
                  rows: g.items.map((r) => ({ kind: r.kind, id: r.id, value: r.value })),
                  total: g.total,
                  client: { name: g.name, phone: g.phone },
                })
              }
              onCobrancaForClient={() => {
                const brFrom = dateFrom ? dateFrom.split("-").reverse().join("/") : "";
                const brTo = dateTo ? dateTo.split("-").reverse().join("/") : "";
                const selectedInGroup = g.items.filter((r) => selected.has(`${r.kind}-${r.id}`));
                const useItems = selectedInGroup.length > 0 ? selectedInGroup : g.items;
                onOpenCobranca({
                  client: { name: g.name, phone: g.phone },
                  rows: useItems.map((r) => ({
                    kind: r.kind,
                    id: r.id,
                    orderNumber: r.orderNumber || "",
                    imei: r.imei || "",
                    date: r.exitDate || r.entryDate || "",
                    description: r.desc || "",
                    items: r.items || [],
                    value: r.value,
                  })),
                  period: { from: brFrom, to: brTo },
                });
              }}
            />
          ))}
        </div>
      )}

      {payFor && (
        <PayModal
          rows={payFor.rows}
          total={payFor.total}
          client={payFor.client}
          onClose={() => setPayFor(null)}
          onDone={() => { setSelected(new Set()); setPayFor(null); onRefresh && onRefresh(); }}
        />
      )}
    </div>
  );
}

function PendingClientGroup({ group, selected, onToggleSelect, onOpenClient, onPaySingle, onPayAllForClient, onCobrancaForClient }) {
  const [open, setOpen] = useState(group.overdueCount > 0);
  const selectedInGroup = group.items.filter((r) => selected && selected.has(`${r.kind}-${r.id}`)).length;
  return (
    <div className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
      group.overdueCount > 0 ? "border-rose-200" : "border-slate-200"
    }`} data-testid={`pending-group-${group.name}`}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left"
          data-testid={`pending-toggle-${group.name}`}
        >
          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
          <div>
            <div className="font-heading font-semibold text-slate-900 text-sm flex items-center gap-2">
              {group.name}
              {group.overdueCount > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200"
                  data-testid="overdue-badge"
                >
                  <AlertTriangle className="w-3 h-3" />
                  {group.overdueCount} atrasado{group.overdueCount > 1 ? "s" : ""} · +{group.maxDaysLate}d
                </span>
              )}
              {selectedInGroup > 0 && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
                  {selectedInGroup} selecionado{selectedInGroup > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-400">
              {group.phone || "—"} · {group.items.length} item{group.items.length > 1 ? "s" : ""}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`font-heading font-bold tabular-nums text-lg ${group.overdueCount > 0 ? "text-rose-700" : "text-slate-800"}`}>
            R$ {fmtBRL(group.total)}
          </div>
          <button
            onClick={() => onOpenClient({ name: group.name, phone: group.phone, id: `grp-${group.name}` })}
            className="h-8 px-2.5 rounded-md text-[11px] font-semibold bg-white border border-slate-200 text-slate-700 hover:border-blue-400 hover:text-blue-700 inline-flex items-center gap-1 transition-all"
            data-testid="open-client-closure"
            title="Abrir fechamento do cliente"
          >
            Abrir
          </button>
          {onCobrancaForClient && (
            <button
              onClick={onCobrancaForClient}
              className="h-8 px-3 rounded-md text-[11px] font-semibold bg-white border-2 border-blue-500 text-blue-700 hover:bg-blue-600 hover:text-white inline-flex items-center gap-1 transition-all"
              data-testid={`pending-cobranca-client-${group.name}`}
              title={selectedInGroup > 0 ? `Gerar PDF com os ${selectedInGroup} itens selecionados deste cliente` : "Gerar PDF com todos os itens deste cliente"}
            >
              <Printer className="w-3 h-3" /> PDF {selectedInGroup > 0 ? `(${selectedInGroup})` : ""}
            </button>
          )}
          <button
            onClick={onPayAllForClient}
            className="h-8 px-3 rounded-md text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1 transition-all"
            data-testid="pay-all-client"
          >
            <CheckCircle2 className="w-3 h-3" /> Fechar tudo
          </button>
        </div>
      </div>

      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: HEADER_BG }}>
                <th className="w-10 px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-white">
                  <input
                    type="checkbox"
                    checked={group.items.length > 0 && group.items.every((r) => selected && selected.has(`${r.kind}-${r.id}`))}
                    onChange={() => {
                      const allSel = group.items.every((r) => selected && selected.has(`${r.kind}-${r.id}`));
                      group.items.forEach((r) => {
                        const isSel = selected && selected.has(`${r.kind}-${r.id}`);
                        if (allSel && isSel) onToggleSelect && onToggleSelect(r);
                        else if (!allSel && !isSel) onToggleSelect && onToggleSelect(r);
                      });
                    }}
                    data-testid={`pending-select-all-${group.name}`}
                    className="w-3.5 h-3.5 cursor-pointer accent-blue-600"
                  />
                </th>
                {["TIPO", "DATA SAÍ.", "DESCRIÇÃO", "VALOR (R$)", "STATUS", ""].map((h) => (
                  <th key={h} className="text-left text-[10px] font-bold uppercase tracking-wider text-white px-3 py-2 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {group.items.map((r) => {
                const isSel = selected && selected.has(`${r.kind}-${r.id}`);
                return (
                <tr key={`${r.kind}-${r.id}`} className={`border-t border-slate-100 hover:bg-blue-50/30 ${r.overdue ? "bg-rose-50/40" : ""} ${isSel ? "bg-blue-50" : ""}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={!!isSel}
                      onChange={() => onToggleSelect && onToggleSelect(r)}
                      data-testid={`pending-select-${r.kind}-${r.id}`}
                      className="w-4 h-4 cursor-pointer accent-blue-600"
                    />
                  </td>
                  <td className="px-3 py-2 text-[11px]">
                    <span className={`px-1.5 py-0.5 rounded font-semibold ${
                      r.kind === "order" ? "bg-blue-50 text-blue-700 border border-blue-100" : "bg-purple-50 text-purple-700 border border-purple-100"
                    }`}>
                      {r.kind === "order" ? (r.orderNumber || "OS") : "Peça"}
                    </span>
                    {r.imei && (
                      <div className="text-[10px] text-slate-500 tabular-nums mt-0.5" title="IMEI ou OS">{r.imei}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap tabular-nums">{r.exitDate || r.entryDate || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">{r.desc || "—"}</td>
                  <td className="px-3 py-2 text-xs font-semibold text-slate-800 tabular-nums whitespace-nowrap">R$ {fmtBRL(r.value)}</td>
                  <td className="px-3 py-2">
                    {r.overdue ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 whitespace-nowrap" data-testid="row-overdue-badge">
                        <AlertTriangle className="w-3 h-3" />
                        Atrasado +{r.daysLate}d
                      </span>
                    ) : (
                      <span className="text-[10px] italic text-slate-400">Em aberto</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => onPaySingle(r)}
                      data-testid={`pending-pay-${r.kind}-${r.id}`}
                      className="h-7 px-2.5 rounded-md text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all inline-flex items-center gap-1"
                    >
                      <CheckCircle2 className="w-3 h-3" /> Pago
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
